// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). Shared workflow effects.
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { DiagnosisDataEntryService } from '../../../services/diagnosis-data-entry.service';
import { DiagnosisSessionService } from '../../../services/diagnosis-session.service';
import { DiagnosisSolutionService } from '../../../services/diagnosis-solution.service';
import { SelectChallengeService } from '../../../services/select-challenge.service';

import { ManageSessionService } from '../../../admin-features/services/diagnosis-session/manage-session.service';
import { CURRENT_STATUS } from '../../../common/constants/status.constant';
import { Mode3ContextService } from '../../../services/mode3-context.service';
import { RouterService } from '../../../services/router.service';
import {
  AdditionalAPIActions,
  addPairChallenge,
  CustomerSpecificationActions,
  CustomerSpecificationAPIActions,
  deletePairChallenges,
  filterSubChallenge,
  initializeSession,
  initializeSessionFailure,
  initializeSessionSuccess,
  loadChallengesByPSL,
  LoadChallengesFailure,
  LoadChallengesSuccess,
  loadDataByDiagnosisId,
  loadPairChallengesFailure,
  loadSeletedChallengesFailure,
  loadSeletedChallengesSuccess,
  SessionActions,
  SessionAPIActions,
  setPairChallenges,
  SolutionActions,
  SolutionAPIActions,
  updatePairChallenge,
  VDRJobAPIActions,
} from '../actions/workflow.actions';

@Injectable()
export class WorkflowEffects {
  #store = inject(Store);
  #routerService = inject(RouterService);
  readonly #mode3Context = inject(Mode3ContextService);
  readonly #actions = inject(Actions);
  readonly #selectChallengeService = inject(SelectChallengeService);
  readonly #diagnosisSessionService = inject(DiagnosisSessionService);
  readonly #manageSessionService = inject(ManageSessionService);
  readonly #diagnosisSolutionService = inject(DiagnosisSolutionService);
  readonly #diagnosisDataEntryService = inject(DiagnosisDataEntryService);

  loadVDRJobInformation$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      // Mode 3 sessions never have an associated VDRDiagnosis row (Mode 3 doesn't touch that
      // table), so this call is a guaranteed 404 there — skip it instead of logging a false-alarm error.
      filter(() => !this.#mode3Context.projectId()),
      // switchMap: a newer session load must cancel any still-in-flight request for the
      // previous session, or its late response can incorrectly populate the new session's view.
      switchMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.getRelatedVDRJob(diagnosisId).pipe(
          map((vdrJob) =>
            VDRJobAPIActions.loadVDRJobInformationSuccess({ vdrJob }),
          ),
          catchError((error) => of(VDRJobAPIActions.requestFailure({ error }))),
        ),
      ),
    ),
  );

  loadChallengesByPSL$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadChallengesByPSL),
      exhaustMap(({ pslId }) =>
        this.#selectChallengeService.getChallengesByPSL(pslId).pipe(
          map((challenges) => LoadChallengesSuccess({ challenges })),
          catchError((error) => of(LoadChallengesFailure({ error }))),
        ),
      ),
    ),
  );

  loadSeletedChallenges$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      switchMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.getSessionChallenges(diagnosisId).pipe(
          map((diagnosisChallenges) =>
            loadSeletedChallengesSuccess({ diagnosisChallenges }),
          ),
          catchError((error) => of(loadSeletedChallengesFailure({ error }))),
        ),
      ),
    ),
  );

  initializeSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(initializeSession),
      exhaustMap(({ initialSession }) =>
        this.#diagnosisSessionService.initializeSession(initialSession).pipe(
          tap((diagnosisSession) => {
            this.#routerService.goToSolutionSelection(diagnosisSession.id);
          }),
          map((diagnosisSession) =>
            initializeSessionSuccess({ diagnosisSession }),
          ),
          catchError((error) => of(initializeSessionFailure({ error }))),
        ),
      ),
    ),
  );

  completeOneOffSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.completeOneOffSession),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.completeOneOffSession(diagnosisId).pipe(
          map((session) =>
            SessionAPIActions.completeOneOffSessionSuccess({ session }),
          ),
          catchError((error) =>
            of(
              SessionAPIActions.completeOneOffSessionFailure({
                modifiedInfo: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  completeVDRSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.completeVDRSession),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.completeVDRSession(diagnosisId).pipe(
          map((session) =>
            SessionAPIActions.completeVDRSessionSuccess({ session }),
          ),
          catchError((error) =>
            of(
              SessionAPIActions.completeVDRSessionFailure({
                modifiedInfo: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  completeDiagnosisModeThree$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.completeDiagnosisModeThree),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisSessionService
          .completeDiagnosisModeThree(diagnosisId)
          .pipe(
            map((session) =>
              SessionAPIActions.completeDiagnosisModeThreeSuccess({
                session,
              }),
            ),
            catchError((error) =>
              of(
                SessionAPIActions.completeDiagnosisModeThreeFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  resetSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.resetSession),
      exhaustMap(({ diagnosisId, pslCode }) =>
        this.#diagnosisSessionService.resetSession(diagnosisId, pslCode).pipe(
          map((session) => SessionAPIActions.resetSessionSuccess({ session })),
          catchError((error) =>
            of(SessionAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  resetChallengeByPSL$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.resetSessionSuccess),
      map(() => filterSubChallenge({ challengeId: '' })),
    ),
  );

  reopenSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.reopenSession),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.back(diagnosisId).pipe(
          map((session) => SessionAPIActions.reopenSessionSuccess({ session })),
          catchError((error) =>
            of(
              SessionAPIActions.reopenSessionFailure({
                modifiedInfo: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  resetChallengeWhenReopenSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.reopenSessionSuccess),
      map(() => filterSubChallenge({ challengeId: '' })),
    ),
  );

  cancelSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.cancelSession),
      exhaustMap(({ diagnosisId }) =>
        this.#manageSessionService.cancel(diagnosisId).pipe(
          map((session) => SessionAPIActions.cancelSessionSuccess({ session })),
          catchError((error) =>
            of(
              SessionAPIActions.reopenSessionFailure({
                modifiedInfo: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSessionInformation$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      switchMap(({ diagnosisId }) =>
        this.#diagnosisSessionService.getSessionInfoById(diagnosisId).pipe(
          map((session) =>
            SessionAPIActions.loadSessionInformationSuccess({ session }),
          ),
          catchError((error) =>
            of(SessionAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  addPairChallenge$ = createEffect(() =>
    this.#actions.pipe(
      ofType(addPairChallenge),
      exhaustMap(({ diagnosisId, subChallengeId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        this.#store.dispatch(
          CustomerSpecificationAPIActions.setDataEntryLoadingState(),
        );
        return this.#diagnosisSessionService
          .addPairChallenges({
            diagnosisId,
            subChallengeId,
          })
          .pipe(
            map(() => setPairChallenges({ diagnosisId, subChallengeId })),
            catchError((error) =>
              of(loadPairChallengesFailure({ modifiedInfo: error.message })),
            ),
          );
      }),
    ),
  );

  deletePairChallenges$ = createEffect(() =>
    this.#actions.pipe(
      ofType(deletePairChallenges),
      exhaustMap(({ diagnosisId, subChallengeId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        this.#store.dispatch(
          CustomerSpecificationAPIActions.setDataEntryLoadingState(),
        );
        return this.#diagnosisSessionService
          .removePairChallenge({
            diagnosisId,
            subChallengeId,
          })
          .pipe(
            map(() => updatePairChallenge({ diagnosisId, subChallengeId })),
            catchError((error) =>
              of(loadPairChallengesFailure({ modifiedInfo: error.message })),
            ),
          );
      }),
    ),
  );

  updateDataEntryCategoriesAfterAddPairChallenge$ = createEffect(() =>
    this.#actions.pipe(
      ofType(setPairChallenges),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisDataEntryService
          .getSuggestedDataEntryCategories(diagnosisId)
          .pipe(
            map((dataEntries) =>
              CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess({
                diagnosisId,
                dataEntries,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  updateDataEntryCategoriesAfterRemovePairChallenge$ = createEffect(() =>
    this.#actions.pipe(
      ofType(updatePairChallenge),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisDataEntryService
          .getSuggestedDataEntryCategories(diagnosisId)
          .pipe(
            map((dataEntries) =>
              CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess({
                diagnosisId,
                dataEntries,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          ),
      ),
    ),
  );

  removeEntryValue$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationActions.removeEntryValue),
      exhaustMap(({ entryValue }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisDataEntryService
          .removeEntryValue(entryValue)
          .pipe(
            map((solutions) =>
              CustomerSpecificationAPIActions.removeEntryValueSuccess({
                solutions,
              }),
            ),
            catchError((error) =>
              of(
                CustomerSpecificationAPIActions.removeEntryValueFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  removeAllEntryValues$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationActions.removeAllEntryValues),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisDataEntryService
          .resetEntryValue(diagnosisId)
          .pipe(
            map((solutions) =>
              CustomerSpecificationAPIActions.removeAllEntryValuesSuccess({
                diagnosisId,
                solutions,
              }),
            ),
            catchError((error) =>
              of(
                CustomerSpecificationAPIActions.removeAllEntryValuesFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  removeAllEntryValuesSuccess$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationAPIActions.removeAllEntryValuesSuccess),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisDataEntryService
          .getSuggestedDataEntryCategories(diagnosisId)
          .pipe(
            map((dataEntries) =>
              CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess({
                diagnosisId,
                dataEntries,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadSuggestedDataEntries$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      switchMap(({ diagnosisId }) => {
        this.#store.dispatch(
          CustomerSpecificationAPIActions.setDataEntryLoadingState(),
        );
        return this.#diagnosisDataEntryService
          .getSuggestedDataEntryCategories(diagnosisId)
          .pipe(
            map((dataEntries) =>
              CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess({
                diagnosisId,
                dataEntries,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadEnteredDataEntries$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess),
      switchMap(({ diagnosisId }) => {
        return this.#diagnosisDataEntryService
          .verifyEnteredValues(diagnosisId)
          .pipe(
            map((enteredValues) =>
              CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess({
                diagnosisId,
                enteredValues,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadEnteredDataEntriesOnceVDRSessionCompleted$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.completeVDRSession),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(
          CustomerSpecificationAPIActions.setDataEntryLoadingState(),
        );
        return this.#diagnosisDataEntryService
          .verifyEnteredValues(diagnosisId)
          .pipe(
            map((enteredValues) =>
              CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess({
                diagnosisId,
                enteredValues,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadEnteredDataEntriesOnceOneOffSessionCompleted$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeOneOffSessionSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          CustomerSpecificationAPIActions.setDataEntryLoadingState(),
        );
        return this.#diagnosisDataEntryService
          .verifyEnteredValues(session.id)
          .pipe(
            map((enteredValues) =>
              CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess({
                diagnosisId: session.id,
                enteredValues,
              }),
            ),
            catchError((error) =>
              of(CustomerSpecificationAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadAvailableSolutions$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess),
      switchMap(({ diagnosisId }) =>
        this.#diagnosisSolutionService.getAvailableSolutions(diagnosisId).pipe(
          map((solutionCompatibilities) =>
            SolutionAPIActions.getAvailableSolutionsSuccess({
              solutions: solutionCompatibilities,
            }),
          ),
          catchError((error) =>
            of(SolutionAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  loadSelectedSolutions$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess),
      switchMap(({ diagnosisId }) =>
        this.#diagnosisSolutionService.getSelectedSolutions(diagnosisId).pipe(
          map((solutionCompatibilities) =>
            SolutionAPIActions.getSelectedSolutionsSuccess({
              solutionIds: solutionCompatibilities.map(
                (solution) => solution.availableSolution.id,
              ),
            }),
          ),
          catchError((error) =>
            of(CustomerSpecificationAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  upsertEntryValue$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationActions.upsertEntryValue),
      switchMap(({ entryValue }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisDataEntryService
          .upsertEntryValue(entryValue)
          .pipe(
            map((solutions) =>
              CustomerSpecificationAPIActions.upsertEntryValueSuccess({
                diagnosisId: entryValue.diagnosisId,
                solutions,
              }),
            ),
            catchError((error) =>
              of(
                CustomerSpecificationAPIActions.upsertEntryValueFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  updateSeletedSolutions$ = createEffect(() =>
    this.#actions.pipe(
      ofType(CustomerSpecificationAPIActions.upsertEntryValueSuccess),
      exhaustMap(({ diagnosisId }) =>
        this.#diagnosisSolutionService.getSelectedSolutions(diagnosisId).pipe(
          map((solutionCompatibilities) =>
            SolutionAPIActions.getSelectedSolutionsSuccess({
              solutionIds: solutionCompatibilities.map(
                (solution) => solution.availableSolution.id,
              ),
            }),
          ),
          catchError((error) =>
            of(SolutionAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  chooseSolution$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(SolutionActions.chooseSolution),
      exhaustMap(({ diagnosisId, solutionId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisSolutionService
          .selectSolution({ diagnosisId, solutionId })
          .pipe(
            map((solutionCompatibilities) => {
              return SolutionAPIActions.chooseSolutionSuccess({
                solutions: solutionCompatibilities,
                selectedSolutionId: solutionId,
              });
            }),
            catchError((error) =>
              of(
                SolutionAPIActions.chooseSolutionFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          );
      }),
    );
  });

  removeSolution$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(SolutionActions.removeSolution),
      exhaustMap(({ diagnosisId, solutionId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());

        return this.#diagnosisSolutionService
          .unselectSolution({ diagnosisId, solutionId })
          .pipe(
            map((solutionCompatibilities) =>
              SolutionAPIActions.removeSolutionSuccess({
                solutions: solutionCompatibilities,
                selectedSolutionId: solutionId,
              }),
            ),
            catchError((error) =>
              of(
                SolutionAPIActions.removeSolutionFailure({
                  modifiedInfo: error.message,
                }),
              ),
            ),
          );
      }),
    );
  });

  removeAllSolutions$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(SolutionActions.removeAllSolutions),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisSolutionService
          .resetSolutionSelection(diagnosisId)
          .pipe(
            map((solutionCompatibilities) => {
              return SolutionAPIActions.removeAllSolutionsSuccess({
                solutions: solutionCompatibilities,
              });
            }),
            catchError((error) => {
              return of(
                SolutionAPIActions.removeAllSolutionsFailure({
                  modifiedInfo: error.message,
                }),
              );
            }),
          );
      }),
    );
  });

  loadSolutionCompatibilities$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      switchMap(({ diagnosisId }) => {
        this.#store.dispatch(SolutionAPIActions.setSolutionLoadingState());
        return this.#diagnosisSolutionService
          .getCombinedCompatibilities(diagnosisId)
          .pipe(
            map((solutionCompatibilities) =>
              SolutionAPIActions.getAvailableSolutionsSuccess({
                solutions: solutionCompatibilities,
              }),
            ),
            catchError((error) =>
              of(SolutionAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadSolutionReferences$ = createEffect(() =>
    this.#actions.pipe(
      // Keyed off the session-info response itself (not loadDataByDiagnosisId +
      // concatLatestFrom(selectSessionStatus)) so this always reflects THIS session's real
      // status instead of a stale value left over from whichever session was viewed previously.
      ofType(SessionAPIActions.loadSessionInformationSuccess),
      filter(
        ({ session }) =>
          session.diagnosisStatusName !== CURRENT_STATUS.IN_PROGRESS,
      ),
      switchMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setSolutionReferenceLoadingState(),
        );
        return this.#diagnosisSessionService
          .getDiagnosisReferences(session.id)
          .pipe(
            map((references) => {
              return AdditionalAPIActions.loadSolutionReferencesSuccess({
                references,
              });
            }),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadSolutionReferencesOnceCompleteOneOffSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeOneOffSessionSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setSolutionReferenceLoadingState(),
        );
        return this.#diagnosisSessionService
          .getDiagnosisReferences(session.id)
          .pipe(
            map((references) =>
              AdditionalAPIActions.loadSolutionReferencesSuccess({
                references,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadSolutionReferencesOnceCompleteVDRSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionActions.completeVDRSession),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setSolutionReferenceLoadingState(),
        );
        return this.#diagnosisSessionService
          .getDiagnosisReferences(diagnosisId)
          .pipe(
            map((references) =>
              AdditionalAPIActions.loadSolutionReferencesSuccess({
                references,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadRequiredActions$ = createEffect(() =>
    this.#actions.pipe(
      // Same fix as loadSolutionReferences$ above — key off the session-info response itself
      // instead of a possibly-stale store read.
      ofType(SessionAPIActions.loadSessionInformationSuccess),
      filter(
        ({ session }) =>
          session.diagnosisStatusName !== CURRENT_STATUS.IN_PROGRESS,
      ),
      switchMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setRequiredActionLoadingState(),
        );
        return this.#diagnosisSessionService
          .getAttachedRequiredActions(session.id)
          .pipe(
            map((requiredActions) =>
              AdditionalAPIActions.loadRequiredActionsSuccess({
                requiredActions,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  // Complete Session

  loadRequiredActionsOnceOneOffSessionCompleted$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeOneOffSessionSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setRequiredActionLoadingState(),
        );
        return this.#diagnosisSessionService
          .getAttachedRequiredActions(session.id)
          .pipe(
            map((requiredActions) =>
              AdditionalAPIActions.loadRequiredActionsSuccess({
                requiredActions,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadRequiredActionsOnceVDRSessionCompleted$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeVDRSessionSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setRequiredActionLoadingState(),
        );
        return this.#diagnosisSessionService
          .getAttachedRequiredActions(session.id)
          .pipe(
            map((requiredActions) =>
              AdditionalAPIActions.loadRequiredActionsSuccess({
                requiredActions,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  // Mode 3 only: mirrors the VDR-session post-completion effects above, but keyed off
  // completeDiagnosisModeThreeSuccess instead of completeVDRSessionSuccess.
  loadSolutionReferencesOnceCompleteModeThreeSession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeDiagnosisModeThreeSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setSolutionReferenceLoadingState(),
        );
        return this.#diagnosisSessionService
          .getDiagnosisReferences(session.id)
          .pipe(
            map((references) =>
              AdditionalAPIActions.loadSolutionReferencesSuccess({
                references,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );

  loadRequiredActionsOnceModeThreeSessionCompleted$ = createEffect(() =>
    this.#actions.pipe(
      ofType(SessionAPIActions.completeDiagnosisModeThreeSuccess),
      exhaustMap(({ session }) => {
        this.#store.dispatch(
          AdditionalAPIActions.setRequiredActionLoadingState(),
        );
        return this.#diagnosisSessionService
          .getAttachedRequiredActions(session.id)
          .pipe(
            map((requiredActions) =>
              AdditionalAPIActions.loadRequiredActionsSuccess({
                requiredActions,
              }),
            ),
            catchError((error) =>
              of(AdditionalAPIActions.requestFailure({ error })),
            ),
          );
      }),
    ),
  );
}
